import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Bot, Send, X, Square, Copy, Check, Minimize2, RotateCcw, MessageSquare, FileText, ExternalLink, Activity, Wrench, Brain, Users, Workflow, List, Plus } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSession } from '@/hooks/useSession';
import { getDocumentDetail, getDocumentPreviewUrl } from '@/features/documents/api';
import { mastraClient } from '@/lib/mastra-client';

// ---------------------------------------------------------------------------
// Types — shared with AgentChatPage
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type AgentStreamMessages = Parameters<ReturnType<typeof mastraClient.getAgent>['stream']>[0];
type AgentStreamMessage = Extract<AgentStreamMessages, readonly unknown[]>[number];
type PanelWireMessage =
  | { role: 'system'; content: string }
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string };

interface Thread {
  id: string;
  title: string;
  agentId: string;
  messages: ChatMessage[];
  createdAt: number;
  trace?: TraceEvent[];
}

type InvocationKind = 'tool' | 'agent' | 'workflow';

type TraceEvent =
  | { kind: 'tool-call'; at: number; name: string; invocation?: InvocationKind; args?: unknown; id?: string }
  | { kind: 'tool-result'; at: number; name: string; invocation?: InvocationKind; output?: unknown; id?: string }
  | { kind: 'reasoning'; at: number; text: string }
  | { kind: 'step'; at: number; phase: 'start' | 'finish'; note?: string }
  | { kind: 'error'; at: number; message: string };

// Classify by name: sub-agent delegations are exposed as `agent-<id>` tools
// by Mastra's supervisor pattern; workflows as `workflow-<id>`. Plain tools
// are everything else.
function classifyInvocation(name: string): InvocationKind {
  if (!name) return 'tool';
  if (name.startsWith('agent-')) return 'agent';
  if (name.startsWith('workflow-') || /^acc-(ar-reconcile|ar-collections|archive-evidence|evidence-search|bank-reconciliation|sync-invoices|vendor-invoice-intake|supplier-invoice-sync)$/.test(name)) return 'workflow';
  return 'tool';
}

// ---------------------------------------------------------------------------
// Config for page-specific agent panels
// ---------------------------------------------------------------------------

const DRAG_THRESHOLD_PX = 5;
const BUBBLE_SIZE = 56;
const PANEL_GAP = 12;

type Variant = 'blue' | 'green' | 'purple';

interface AgentPanelProps {
  agentId: string;
  name: string;
  tagline: string;
  intro: string;                 // greeting text
  placeholder?: string;          // textarea placeholder
  variant?: Variant;             // color theme (default: 'blue')
  storagePrefix: string;         // localStorage namespace
  defaultPosition?: { right: number; bottom: number };
  panelMode?: 'floating' | 'side'; // 'floating' = default popup, 'side' = full-height right drawer
  // Override the Mastra resourceId. Default is the logged-in user's email, which scopes
  // memory per user. Override to scope per entity — e.g. `account-{uuid}` so an Account
  // Agent has dedicated working memory per customer that persists across all conversations
  // about that customer.
  resourceId?: string;
  // Optional system-style primer prepended to every request (not stored in the thread).
  // Use it to ground the agent in entity-specific context (e.g. "You are working on
  // account A-0042 (UUID: ...). All questions are about this account.").
  contextPrimer?: string;
}

const VARIANT_COLORS: Record<Variant, {
  bubble: string;           // floating button
  iconBg: string;           // header bot icon background
  iconFg: string;           // header bot icon fg
  emptyIconBg: string;      // empty-state icon bg
  emptyIconBorder: string;  // empty-state icon border
  emptyIconFg: string;      // empty-state icon fg
  userMsg: string;          // user message bubble
  link: string;             // markdown link color
  blockquote: string;       // markdown blockquote border
  focus: string;            // textarea focus ring
  sendBtn: string;          // send button
  typingDot: string;        // streaming dots
}> = {
  blue: {
    bubble: 'bg-blue-600 hover:bg-blue-500',
    iconBg: 'bg-blue-100', iconFg: 'text-blue-600',
    emptyIconBg: 'bg-blue-50', emptyIconBorder: 'border-blue-200', emptyIconFg: 'text-blue-500',
    userMsg: 'bg-blue-600',
    link: 'text-blue-600', blockquote: 'border-blue-300',
    focus: 'focus-within:border-blue-400 focus-within:ring-blue-100',
    sendBtn: 'bg-blue-600 hover:bg-blue-500',
    typingDot: 'bg-blue-500',
  },
  green: {
    bubble: 'bg-green-600 hover:bg-green-500',
    iconBg: 'bg-green-100', iconFg: 'text-green-600',
    emptyIconBg: 'bg-green-50', emptyIconBorder: 'border-green-200', emptyIconFg: 'text-green-500',
    userMsg: 'bg-green-600',
    link: 'text-green-600', blockquote: 'border-green-300',
    focus: 'focus-within:border-green-400 focus-within:ring-green-100',
    sendBtn: 'bg-green-600 hover:bg-green-500',
    typingDot: 'bg-green-500',
  },
  purple: {
    bubble: 'bg-purple-600 hover:bg-purple-500',
    iconBg: 'bg-purple-100', iconFg: 'text-purple-600',
    emptyIconBg: 'bg-purple-50', emptyIconBorder: 'border-purple-200', emptyIconFg: 'text-purple-500',
    userMsg: 'bg-purple-600',
    link: 'text-purple-600', blockquote: 'border-purple-300',
    focus: 'focus-within:border-purple-400 focus-within:ring-purple-100',
    sendBtn: 'bg-purple-600 hover:bg-purple-500',
    typingDot: 'bg-purple-500',
  },
};

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function toAgentStreamMessage(message: PanelWireMessage): AgentStreamMessage {
  switch (message.role) {
    case 'system':
      return { role: 'system', content: message.content };
    case 'user':
      return { role: 'user', content: message.content };
    case 'assistant':
      return { role: 'assistant', content: message.content };
  }
}

function getLocalStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && typeof window.localStorage?.getItem === 'function') {
      return window.localStorage;
    }
  } catch { /* ignore */ }

  try {
    if (typeof globalThis.localStorage?.getItem === 'function') {
      return globalThis.localStorage;
    }
  } catch { /* ignore */ }

  return null;
}

function getStorageItem(key: string): string | null {
  try {
    return getLocalStorage()?.getItem(key) ?? null;
  } catch { return null; }
}

function setStorageItem(key: string, value: string) {
  try { getLocalStorage()?.setItem(key, value); } catch { /* ignore */ }
}

function removeStorageItem(key: string) {
  try { getLocalStorage()?.removeItem(key); } catch { /* ignore */ }
}

// LLMs (especially Gemma-family) sometimes emit LaTeX-style inline math for
// things that have a direct Unicode equivalent (arrows, operators, spacers,
// bold). We don't bundle KaTeX — instead we rewrite the common macros to
// their Unicode equivalents before markdown rendering.
const LATEX_INLINE = [
  [/\$\s*\\rightarrow\s*\$|\$\\to\$/g, '→'],
  [/\$\s*\\leftarrow\s*\$/g, '←'],
  [/\$\s*\\Rightarrow\s*\$/g, '⇒'],
  [/\$\s*\\Leftarrow\s*\$/g, '⇐'],
  [/\$\s*\\leftrightarrow\s*\$/g, '↔'],
  [/\$\s*\\cdot\s*\$/g, '·'],
  [/\$\s*\\times\s*\$/g, '×'],
  [/\$\s*\\approx\s*\$/g, '≈'],
  [/\$\s*\\equiv\s*\$/g, '≡'],
  [/\$\s*\\pm\s*\$/g, '±'],
  [/\$\s*\\geq\s*\$/g, '≥'],
  [/\$\s*\\leq\s*\$/g, '≤'],
  [/\$\s*\\neq\s*\$/g, '≠'],
  [/\$\s*\\infty\s*\$/g, '∞'],
  [/\$\s*\\alpha\s*\$/g, 'α'],
  [/\$\s*\\beta\s*\$/g, 'β'],
  [/\$\s*\\gamma\s*\$/g, 'γ'],
  [/\$\s*\\delta\s*\$/g, 'δ'],
  [/\$\s*\\sigma\s*\$/g, 'σ'],
  [/\$\s*\\mu\s*\$/g, 'μ'],
  [/\$\s*\\pi\s*\$/g, 'π'],
  [/\$\s*\\quad\s*\$/g, ' '],
  [/\$\s*\\qquad\s*\$/g, '  '],
  // $\mathbf{...}$ → **...**
  [/\$\s*\\mathbf\{([^{}]+)\}\s*\$/g, '**$1**'],
  // $\mathit{...}$ → *...*
  [/\$\s*\\mathit\{([^{}]+)\}\s*\$/g, '*$1*'],
  // $\text{...}$ → ...
  [/\$\s*\\text\{([^{}]+)\}\s*\$/g, '$1'],
] as const;

function normalizeAgentMarkdown(input: string): string {
  let out = input;
  for (const [re, replacement] of LATEX_INLINE) {
    out = out.replace(re as RegExp, replacement as string);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-gray-400 hover:text-gray-600 p-0.5 rounded" title="Copy">
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component — AgentPanel
//
// Canonical in-app agent UI. One component, many mount sites. Every agent
// surface in the app renders this with different props:
//   - AR BookKeeper (AR page):       <AgentPanel agentId="bookkeeper" variant="green" panelMode="side" ... />
//   - Account Agent and Tender Agent: entity-specific panels
//
// All features (chat, References tab, Trace tab, inline DMS preview, input
// draft persistence, streaming tool-call capture) live here so every mount
// site inherits them automatically. Do NOT fork a second chat component —
// extend this one with props or a new `variant` / `panelMode`.
//
// ---------------------------------------------------------------------------

export function AgentPanel({
  agentId,
  name,
  tagline,
  intro,
  placeholder,
  variant = 'blue',
  storagePrefix,
  defaultPosition = { right: 24, bottom: 24 },
  panelMode = 'floating',
  resourceId: resourceIdProp,
  contextPrimer,
}: AgentPanelProps) {
  const { user } = useSession();
  const resourceId = resourceIdProp ?? user?.email ?? 'anonymous';

  // Per-instance localStorage keys so multiple bubbles don't collide.
  const THREADS_KEY = `${storagePrefix}agent-threads-${agentId}`;
  const BUBBLE_ACTIVE_KEY = `${storagePrefix}bubble-active-thread-${agentId}`;
  const BUBBLE_POSITION_KEY = `${storagePrefix}bubble-position-${agentId}`;

  const loadThreads = useCallback((): Thread[] => {
    try {
      const raw = getStorageItem(THREADS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }, [THREADS_KEY]);

  const saveThreads = useCallback((threads: Thread[]) => {
    setStorageItem(THREADS_KEY, JSON.stringify(threads));
  }, [THREADS_KEY]);

  const getActiveThreadId = useCallback((): string | null => {
    return getStorageItem(BUBBLE_ACTIVE_KEY);
  }, [BUBBLE_ACTIVE_KEY]);

  const setActiveThreadIdLS = useCallback((id: string | null) => {
    if (id) setStorageItem(BUBBLE_ACTIVE_KEY, id);
    else removeStorageItem(BUBBLE_ACTIVE_KEY);
  }, [BUBBLE_ACTIVE_KEY]);

  const c = VARIANT_COLORS[variant];
  const finalPlaceholder = placeholder ?? `Ask ${name}...`;

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState<{ right: number; bottom: number }>(() => {
    try {
      const raw = getStorageItem(BUBBLE_POSITION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.right === 'number' && typeof parsed?.bottom === 'number') return parsed;
      }
    } catch { /* ignore */ }
    return defaultPosition;
  });
  const [isDragging, setIsDragging] = useState(false);
  // Persist draft input per agent+thread so reloads don't lose typed text.
  const INPUT_DRAFT_KEY = `${storagePrefix}bubble-input-draft-${agentId}`;
  const [input, setInput] = useState<string>(() => {
    return getStorageItem(INPUT_DRAFT_KEY) ?? '';
  });
  useEffect(() => {
    if (input) setStorageItem(INPUT_DRAFT_KEY, input);
    else removeStorageItem(INPUT_DRAFT_KEY);
  }, [INPUT_DRAFT_KEY, input]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    setStorageItem(BUBBLE_POSITION_KEY, JSON.stringify(position));
  }, [BUBBLE_POSITION_KEY, position]);

  // Clamp on window resize so the bubble never ends up fully off-screen
  useEffect(() => {
    const clamp = () => {
      setPosition((p) => ({
        right: Math.max(0, Math.min(window.innerWidth - BUBBLE_SIZE, p.right)),
        bottom: Math.max(0, Math.min(window.innerHeight - BUBBLE_SIZE, p.bottom)),
      }));
    };
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, []);

  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);
  const justDraggedRef = useRef(false);

  const handleDragPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: position.right,
      startBottom: position.bottom,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const handleDragPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      drag.moved = true;
      setIsDragging(true);
    }
    if (!drag.moved) return;
    const maxRight = window.innerWidth - BUBBLE_SIZE;
    const maxBottom = window.innerHeight - BUBBLE_SIZE;
    setPosition({
      right: Math.max(0, Math.min(maxRight, drag.startRight - dx)),
      bottom: Math.max(0, Math.min(maxBottom, drag.startBottom - dy)),
    });
  };

  const handleDragPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const wasMoved = dragRef.current?.moved ?? false;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (wasMoved) {
      justDraggedRef.current = true;
      setIsDragging(false);
    }
  };

  const [threads, setThreads] = useState<Thread[]>(loadThreads);
  const [threadId, setThreadId] = useState<string | null>(() => {
    const saved = getActiveThreadId();
    const allThreads = loadThreads();
    if (saved && allThreads.some((t) => t.id === saved)) return saved;
    return allThreads[0]?.id ?? null;
  });

  const activeThread = threads.find((t) => t.id === threadId) ?? null;
  const messages = activeThread?.messages ?? [];

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  // Persist threads (shared with full chat page)
  useEffect(() => {
    saveThreads(threads);
  }, [threads]);

  // Persist active thread for bubble
  useEffect(() => {
    setActiveThreadIdLS(threadId);
  }, [threadId]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open && !minimized) scrollToBottom();
  }, [messages, open, minimized, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [input]);

  // Focus on open
  useEffect(() => {
    if (open && !minimized) textareaRef.current?.focus();
  }, [open, minimized]);

  // Reset unread when opening
  useEffect(() => {
    if (open && !minimized) setUnread(0);
  }, [open, minimized]);

  // Sync threads from localStorage (in case full page updated them)
  useEffect(() => {
    if (!open) return;
    const stored = loadThreads();
    setThreads(stored);
  }, [open]);

  const updateThreadMessages = (tid: string, updater: (msgs: ChatMessage[]) => ChatMessage[]) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === tid ? { ...t, messages: updater(t.messages) } : t)),
    );
  };

  const stopStreaming = () => { readerRef.current?.cancel().catch(() => { /* swallow */ }); };

  const newChat = () => {
    const thread: Thread = {
      id: generateId(),
      title: 'New conversation',
      agentId: agentId,
      messages: [],
      createdAt: Date.now(),
    };
    setThreads((prev) => [thread, ...prev]);
    setThreadId(thread.id);
  };

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isStreaming) return;

    // Auto-create thread if none
    let tid = threadId;
    if (!tid) {
      const thread: Thread = {
        id: generateId(),
        title: text.length > 40 ? text.slice(0, 40) + '...' : text,
        agentId: agentId,
        messages: [],
        createdAt: Date.now(),
      };
      setThreads((prev) => [thread, ...prev]);
      setThreadId(thread.id);
      tid = thread.id;
    }

    const userMsg: ChatMessage = { role: 'user', content: text };
    const currentThread = threads.find((t) => t.id === tid);
    const currentMessages = currentThread?.messages ?? [];
    const allMessages = [...currentMessages, userMsg];
    const isFirstExchange = currentMessages.length === 0;

    if (isFirstExchange) {
      setThreads((prev) =>
        prev.map((t) =>
          t.id === tid ? { ...t, title: text.length > 40 ? text.slice(0, 40) + '...' : text } : t,
        ),
      );
    }

    updateThreadMessages(tid, () => allMessages);
    setInput('');
    setIsStreaming(true);
    updateThreadMessages(tid, (msgs) => [...msgs, { role: 'assistant', content: '' }]);

    try {
      // Prepend an optional primer as a system message (not persisted). This grounds
      // the agent in entity-specific context (e.g. the active accountId) on every
      // request without polluting the saved conversation.
      const wireMessages: PanelWireMessage[] =
        contextPrimer
          ? [{ role: 'system', content: contextPrimer }, ...allMessages]
          : allMessages;
      const res = await mastraClient.getAgent(agentId).stream(
        wireMessages.map(toAgentStreamMessage),
        {
          memory: { thread: tid, resource: resourceId },
          // Give the agent enough runway to follow through on multi-step
          // reasoning (load brief → call tool → call another tool → answer)
          // without stopping mid-thought.
          maxSteps: 25,
          modelSettings: { temperature: 0 },
        } as unknown as Parameters<ReturnType<typeof mastraClient.getAgent>['stream']>[1],
      );

      if (!res.ok) {
        const errorText = await res.text().catch(() => res.statusText);
        throw new Error(errorText || `Error: ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');
      readerRef.current = reader;

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const event = JSON.parse(raw);
            const type: string = event?.type ?? '';
            const p = event?.payload ?? {};
            if (type === 'text-delta' && p.text) {
              updateThreadMessages(tid, (msgs) => {
                const updated = [...msgs];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, content: last.content + p.text };
                }
                return updated;
              });
            } else if (type === 'start') {
              appendTrace(tid, {
                kind: 'step',
                at: Date.now(),
                phase: 'start',
                note: `run ${String(event?.runId ?? '').slice(0, 8)}`,
              });
            } else if (type === 'step-start' || type === 'step-finish') {
              appendTrace(tid, {
                kind: 'step',
                at: Date.now(),
                phase: type === 'step-start' ? 'start' : 'finish',
                note: p.stepType ?? p.finishReason,
              });
            } else if (type === 'tool-call') {
              const name = p.toolName ?? p.name ?? 'unknown';
              appendTrace(tid, {
                kind: 'tool-call',
                at: Date.now(),
                name,
                invocation: classifyInvocation(name),
                args: p.args ?? p.input,
                id: p.toolCallId ?? p.id,
              });
            } else if (type === 'tool-result') {
              const name = p.toolName ?? p.name ?? 'unknown';
              appendTrace(tid, {
                kind: 'tool-result',
                at: Date.now(),
                name,
                invocation: classifyInvocation(name),
                output: p.output ?? p.result,
                id: p.toolCallId ?? p.id,
              });
            } else if (type === 'reasoning-delta' || type === 'reasoning') {
              const text = p.text ?? p.delta ?? '';
              if (text) appendTrace(tid, { kind: 'reasoning', at: Date.now(), text });
            } else if (type === 'error') {
              appendTrace(tid, { kind: 'error', at: Date.now(), message: String(p.error ?? p.message ?? 'stream error') });
            }
          } catch { /* skip */ }
        }
      }

      if (minimized || !open) setUnread((n) => n + 1);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        updateThreadMessages(tid, (msgs) => {
          const updated = [...msgs];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            updated[updated.length - 1] = {
              ...last,
              content: `Error: ${err instanceof Error ? err.message : 'Failed to get response'}`,
            };
          }
          return updated;
        });
      }
    } finally {
      readerRef.current = null;
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // -------------------------------------------------------------------------
  // References parsing — extract QBO URLs and DMS document UUIDs from the
  // assistant's messages so the side panel can surface them in a tabs view.
  // -------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<'chat' | 'refs' | 'trace' | 'threads'>('chat');

  const appendTrace = (tid: string, ev: TraceEvent) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === tid ? { ...t, trace: [...(t.trace ?? []), ev] } : t)),
    );
  };

  const clearTrace = (tid: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === tid ? { ...t, trace: [] } : t)),
    );
  };

  const activeTrace = activeThread?.trace ?? [];

  // ---------------------------------------------------------------------------
  // Server-backed thread sync — Mastra persists every thread used via memory:{}
  // to `mastra.mastra_threads` in Postgres. We fetch that list so the Threads
  // tab reflects the server as source of truth (works across devices, survives
  // localStorage clears). localStorage stays as a warm cache to avoid flicker.
  // ---------------------------------------------------------------------------

  const refreshServerThreads = useCallback(async () => {
    try {
      const j = await mastraClient.listMemoryThreads({
        agentId,
        resourceId,
        page: 0,
        perPage: 50,
        orderBy: 'updatedAt',
      } as unknown as Parameters<typeof mastraClient.listMemoryThreads>[0]);
      const serverThreads: Array<Record<string, unknown>> = (j as { threads?: Array<Record<string, unknown>> })?.threads ?? [];
      if (serverThreads.length === 0) return;
      setThreads((prev) => {
        const byId = new Map(prev.map((t) => [t.id, t]));
        for (const s of serverThreads) {
          const id = String(s.id);
          const createdAt = new Date(String(s.createdAt ?? s.updatedAt ?? Date.now())).getTime();
          const title = (s.title as string | null) || '';
          const existing = byId.get(id);
          if (existing) {
            // Keep local messages + trace, update title/createdAt if server has better data
            byId.set(id, {
              ...existing,
              title: title || existing.title,
              createdAt: existing.createdAt || createdAt,
            });
          } else {
            byId.set(id, {
              id,
              title: title || 'Untitled',
              agentId,
              messages: [], // lazy-fetched on click
              createdAt,
              trace: [],
            });
          }
        }
        return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
      });
    } catch { /* offline is fine — fall back to local cache */ }
  }, [agentId, resourceId]);

  // Refresh on open + after each generation finishes
  useEffect(() => {
    if (open) refreshServerThreads();
  }, [open, refreshServerThreads]);
  useEffect(() => {
    if (!isStreaming) refreshServerThreads();
  }, [isStreaming, refreshServerThreads]);

  // Lazy-load messages for a thread when user clicks one that has no cached messages
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const selectThread = async (tid: string) => {
    setThreadId(tid);
    setActiveTab('chat');
    const target = threads.find((t) => t.id === tid);
    if (target && target.messages.length > 0) return; // already cached
    setLoadingThreadId(tid);
    try {
      const j = await mastraClient.listThreadMessages(tid, { agentId });
      // Mastra returns { messages: [{ role, content, ... }, ...] } in chronological order
      const r = j as { messages?: Array<Record<string, unknown>>; uiMessages?: Array<Record<string, unknown>> };
      const msgs: Array<Record<string, unknown>> = r?.messages ?? r?.uiMessages ?? [];
      const parsed: ChatMessage[] = msgs
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: typeof m.content === 'string'
            ? m.content
            : Array.isArray(m.content)
              ? m.content.map((p: any) => (typeof p === 'string' ? p : (p?.text ?? ''))).join('')
              : String(m.content ?? ''),
        }))
        .filter((m) => m.content);
      if (parsed.length > 0) {
        setThreads((prev) =>
          prev.map((t) => (t.id === tid ? { ...t, messages: parsed } : t)),
        );
      }
    } catch { /* ignore */ }
    finally { setLoadingThreadId(null); }
  };

  // Load historical spans from Mastra's persisted observability store
  // (`mastra.mastra_traces` in PG). Converts server spans into our TraceEvent
  // shape and merges into the thread's trace array.
  const [loadingHistory, setLoadingHistory] = useState(false);
  const loadHistoricalTrace = async () => {
    if (!threadId) return;
    setLoadingHistory(true);
    try {
      const j = await mastraClient.listTraces({ filters: { threadId } } as unknown as Parameters<typeof mastraClient.listTraces>[0]);
      const spans: Array<Record<string, unknown>> = (j as { spans?: Array<Record<string, unknown>> })?.spans ?? [];
      if (spans.length === 0) {
        window.alert('No persisted trace for this thread yet.');
        return;
      }
      // Sort by start time ascending
      const sorted = spans.slice().sort((a, b) => {
        const ta = new Date(String(a.startedAt)).getTime();
        const tb = new Date(String(b.startedAt)).getTime();
        return ta - tb;
      });
      const historical: TraceEvent[] = [];
      for (const s of sorted) {
        const at = new Date(String(s.startedAt)).getTime() || Date.now();
        const spanType = String(s.spanType ?? '');
        const name = String(s.entityName ?? s.name ?? 'unknown');
        // Map Mastra's spanType to our invocation classification
        const invocation: InvocationKind | undefined =
          spanType === 'tool_call' || spanType === 'llm_generation_tool_call' ? 'tool'
          : spanType === 'agent_run' ? 'agent'
          : spanType === 'workflow_run' || spanType === 'workflow_step' ? 'workflow'
          : undefined;

        if (invocation) {
          historical.push({ kind: 'tool-call', at, name, invocation, args: s.input, id: String(s.spanId) });
          if (s.endedAt) {
            const endedAt = new Date(String(s.endedAt)).getTime() || at;
            historical.push({
              kind: 'tool-result',
              at: endedAt,
              name,
              invocation,
              output: s.output ?? (s.error ? { error: s.error } : undefined),
              id: String(s.spanId),
            });
          }
        } else if (s.error) {
          historical.push({ kind: 'error', at, message: String(s.error) });
        }
      }
      if (historical.length === 0) {
        window.alert('No renderable span events in history.');
        return;
      }
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, trace: [...historical, ...(t.trace ?? [])] } : t)),
      );
    } catch (e) {
      window.alert(`History error: ${(e as Error).message}`);
    } finally {
      setLoadingHistory(false);
    }
  };

  const references = useMemo(() => {
    const qboLinks = new Set<string>();
    const dmsIds = new Set<string>();
    const qboRe = /https:\/\/qbo\.intuit\.com\/app\/[A-Za-z0-9?=&_\-./]+/g;
    // UUID v4 shape — tight enough to avoid false positives most of the time
    const uuidRe = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const match of m.content.match(qboRe) ?? []) qboLinks.add(match);
      for (const match of m.content.match(uuidRe) ?? []) dmsIds.add(match.toLowerCase());
    }
    return { qbo: Array.from(qboLinks), dms: Array.from(dmsIds) };
  }, [messages]);

  const refCount = references.qbo.length + references.dms.length;

  // Lazy DMS metadata fetch (name + created date) so the list is readable
  type DmsDocMeta = { id: string; name?: string; createdAt?: string };
  const [dmsMeta, setDmsMeta] = useState<Record<string, DmsDocMeta>>({});
  useEffect(() => {
    if (activeTab !== 'refs') return;
    const missing = references.dms.filter((id) => !dmsMeta[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const next: Record<string, DmsDocMeta> = {};
      for (const id of missing) {
        try {
          const { data: d } = await getDocumentDetail(id);
          if (d?.id) next[id] = { id, name: d.name, createdAt: d.createdAt };
        } catch { /* ignore — UUID might not be a DMS doc */ }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setDmsMeta((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, references.dms, dmsMeta]);

  // Label a QBO URL with its entity type for nicer display
  const labelQboUrl = (url: string): string => {
    const m = url.match(/\/app\/([^?]+)/);
    const entity = m?.[1] ?? 'link';
    const idMatch = url.match(/(?:txnId|nameId)=([^&]+)/);
    return idMatch ? `${entity} #${idMatch[1]}` : entity;
  };

  // Inline preview state: when set, show an iframe overlay inside the panel.
  const [preview, setPreview] = useState<{ id: string; url: string; name?: string } | null>(null);

  useEffect(() => {
    setDmsMeta({});
    setPreview(null);
  }, [user?.pg_id]);

  // Fetch a signed GCS URL from DMS (/preview-url returns JSON, not a redirect).
  const fetchPreviewUrl = async (id: string): Promise<string | null> => {
    try {
      const result = await getDocumentPreviewUrl(id);
      return result.previewUrl ?? null;
    } catch { return null; }
  };

  const openDmsInlinePreview = async (id: string) => {
    const url = await fetchPreviewUrl(id);
    if (!url) {
      window.alert('DMS preview unavailable');
      return;
    }
    setPreview({ id, url, name: dmsMeta[id]?.name });
  };

  // Open the full DMS document view in a new tab: /documents?id=<docId>
  // shows preview on the left + metadata/markdown/html tabs on the right.
  const openDmsInNewTab = (id: string) => {
    window.open(`/documents?id=${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className={
            panelMode === 'side'
              ? `fixed z-[45] top-0 right-0 bottom-0 bg-white shadow-2xl border-l border-gray-200 flex flex-col transition-all duration-200 ${
                  minimized ? 'w-72 h-12 top-auto bottom-6 right-6 rounded-2xl border' : 'w-[420px]'
                }`
              : `fixed z-[45] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-200 ${
                  minimized ? 'w-72 h-12' : 'w-[420px] h-[600px]'
                }`
          }
          style={
            panelMode === 'side' && !minimized
              ? { top: 0, right: 0, bottom: 0 }
              : {
                  right: `${position.right}px`,
                  bottom: `${position.bottom + BUBBLE_SIZE + PANEL_GAP}px`,
                  maxHeight: 'calc(100vh - 120px)',
                }
          }
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100 rounded-t-2xl bg-gray-50">
            <div className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-lg ${c.iconBg} flex items-center justify-center`}>
                <Bot className={`h-4 w-4 ${c.iconFg}`} />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-900">{name}</span>
                <span className="text-[10px] text-gray-400 ml-1.5">{tagline}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={newChat}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="New chat"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setMinimized(!minimized)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Tabs (side-panel mode only — floating mode stays simple) */}
              {panelMode === 'side' && (
                <div className="shrink-0 flex items-center gap-1 px-2 pt-2 border-b border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setActiveTab('chat')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                      activeTab === 'chat'
                        ? 'bg-white text-gray-900 border border-b-0 border-gray-200 -mb-px'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('refs')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                      activeTab === 'refs'
                        ? 'bg-white text-gray-900 border border-b-0 border-gray-200 -mb-px'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    References
                    {refCount > 0 && (
                      <span className={`ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.iconBg} ${c.iconFg}`}>
                        {refCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('trace')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                      activeTab === 'trace'
                        ? 'bg-white text-gray-900 border border-b-0 border-gray-200 -mb-px'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Tool calls + reasoning timeline"
                  >
                    <Activity className="h-3.5 w-3.5" />
                    Trace
                    {isStreaming && (
                      <span className={`ml-1 inline-block h-1.5 w-1.5 rounded-full ${c.typingDot} animate-pulse`} />
                    )}
                    {!isStreaming && activeTrace.length > 0 && (
                      <span className={`ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.iconBg} ${c.iconFg}`}>
                        {activeTrace.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('threads')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                      activeTab === 'threads'
                        ? 'bg-white text-gray-900 border border-b-0 border-gray-200 -mb-px'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Switch between saved conversations or start a new one"
                  >
                    <List className="h-3.5 w-3.5" />
                    Threads
                    {threads.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                        {threads.length}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* Inline document preview overlay (covers the full panel while open) */}
              {preview && panelMode === 'side' && (
                <div className="absolute inset-0 z-10 flex flex-col bg-white">
                  <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <div className="min-w-0 flex items-center gap-2">
                      <FileText className={`h-4 w-4 ${c.iconFg}`} />
                      <span className="text-xs font-medium text-gray-900 truncate">
                        {preview.name ?? preview.id.slice(0, 8) + '…'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <a
                        href={`/documents?id=${encodeURIComponent(preview.id)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        title="Open full DMS view in new tab"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => setPreview(null)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        title="Close preview"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <iframe
                    src={preview.url}
                    className="flex-1 w-full border-0"
                    title={preview.name ?? 'Document preview'}
                  />
                </div>
              )}

              {/* References tab */}
              {panelMode === 'side' && activeTab === 'refs' && (
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                  {refCount === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-center gap-2 text-gray-400">
                      <FileText className="h-8 w-8" />
                      <p className="text-xs max-w-[240px]">
                        No references yet. As the agent answers, QuickBooks links and DMS documents it cites will appear here.
                      </p>
                    </div>
                  )}
                  {references.qbo.length > 0 && (
                    <section>
                      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                        QuickBooks ({references.qbo.length})
                      </h3>
                      <ul className="space-y-1">
                        {references.qbo.map((url) => (
                          <li key={url}>
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-gray-50 group`}
                            >
                              <span className={`${c.iconFg} truncate`}>{labelQboUrl(url)}</span>
                              <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-gray-600 shrink-0" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {references.dms.length > 0 && (
                    <section>
                      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                        DMS ({references.dms.length})
                      </h3>
                      <ul className="space-y-1">
                        {references.dms.map((id) => {
                          const meta = dmsMeta[id];
                          return (
                            <li key={id} className="group/dms flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-50">
                              <button
                                type="button"
                                onClick={() => openDmsInlinePreview(id)}
                                className="flex-1 min-w-0 text-left text-xs"
                                title="Preview inline"
                              >
                                <span className="truncate block">
                                  <span className="text-gray-900">{meta?.name ?? id.slice(0, 8) + '…'}</span>
                                  {meta?.createdAt && (
                                    <span className="text-gray-400 ml-1.5">
                                      {new Date(meta.createdAt).toISOString().slice(0, 10)}
                                    </span>
                                  )}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => openDmsInNewTab(id)}
                                className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                title="Open in new tab"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  )}
                </div>
              )}

              {/* Trace tab — live timeline of tool calls + reasoning */}
              {panelMode === 'side' && activeTab === 'trace' && (
                <div className="flex-1 overflow-y-auto px-3 py-2 font-mono text-[11px]">
                  {activeTrace.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-center gap-2 text-gray-400">
                      {isStreaming ? (
                        <>
                          <Activity className={`h-8 w-8 ${c.iconFg} animate-pulse`} />
                          <p className="max-w-[240px] font-sans">
                            Waiting for the first event from the agent…
                          </p>
                        </>
                      ) : (
                        <>
                          <Activity className="h-8 w-8" />
                          <p className="max-w-[260px] font-sans">
                            No live trace in this session. Either send a message that triggers tool use, or load persisted spans for this thread from Mastra.
                          </p>
                          <button
                            type="button"
                            onClick={loadHistoricalTrace}
                            disabled={loadingHistory || !threadId}
                            className={`mt-1 font-sans text-xs px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-50 ${c.iconFg}`}
                          >
                            {loadingHistory ? 'Loading…' : 'Load history'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {activeTrace.length > 0 && (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-sans text-xs text-gray-500">
                          {activeTrace.length} event{activeTrace.length === 1 ? '' : 's'}
                        </span>
                        <div className="flex items-center gap-2 font-sans text-[10px]">
                          <button
                            type="button"
                            onClick={loadHistoricalTrace}
                            disabled={loadingHistory}
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                            title="Load persisted spans for this thread from mastra_traces"
                          >
                            {loadingHistory ? 'Loading…' : 'Load history'}
                          </button>
                          <span className="text-gray-200">·</span>
                          <button
                            type="button"
                            onClick={() => threadId && clearTrace(threadId)}
                            className="text-gray-400 hover:text-gray-700"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <ol className="space-y-1.5">
                        {activeTrace.map((ev, i) => (
                          <li key={i} className="border-l-2 border-gray-200 pl-2">
                            {(ev.kind === 'tool-call' || ev.kind === 'tool-result') && (() => {
                              const inv = ev.invocation ?? classifyInvocation(ev.name);
                              const icon = inv === 'agent'
                                ? <Users className="h-3 w-3 text-indigo-500" />
                                : inv === 'workflow'
                                  ? <Workflow className="h-3 w-3 text-amber-500" />
                                  : <Wrench className={`h-3 w-3 ${c.iconFg}`} />;
                              const nameClass = inv === 'agent'
                                ? 'text-indigo-600'
                                : inv === 'workflow'
                                  ? 'text-amber-600'
                                  : c.iconFg;
                              const badgeLabel = inv === 'agent' ? 'delegate' : inv === 'workflow' ? 'workflow' : 'tool';
                              const isResult = ev.kind === 'tool-result';
                              return (
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    {isResult ? <Check className="h-3 w-3 text-gray-500" /> : icon}
                                    <span className={`font-semibold ${isResult ? 'text-gray-700' : nameClass}`}>{ev.name}</span>
                                    <span className={`font-sans text-[9px] uppercase tracking-wide px-1 rounded ${
                                      inv === 'agent' ? 'bg-indigo-50 text-indigo-600' :
                                      inv === 'workflow' ? 'bg-amber-50 text-amber-600' :
                                      'bg-gray-100 text-gray-500'
                                    }`}>
                                      {badgeLabel}
                                    </span>
                                    <span className="text-gray-400 font-sans text-[10px]">{isResult ? 'result' : 'call'}</span>
                                  </div>
                                  {!isResult && ev.args !== undefined && (
                                    <pre className="mt-0.5 text-gray-600 whitespace-pre-wrap break-all leading-tight">
                                      {JSON.stringify(ev.args, null, 2).slice(0, 400)}
                                    </pre>
                                  )}
                                  {isResult && ev.output !== undefined && (
                                    <pre className="mt-0.5 text-gray-500 whitespace-pre-wrap break-all leading-tight max-h-24 overflow-y-auto">
                                      {typeof ev.output === 'string'
                                        ? ev.output.slice(0, 400)
                                        : JSON.stringify(ev.output, null, 2).slice(0, 400)}
                                    </pre>
                                  )}
                                </div>
                              );
                            })()}
                            {ev.kind === 'reasoning' && (
                              <div className="flex items-start gap-1.5 text-gray-600">
                                <Brain className="h-3 w-3 mt-0.5 text-amber-500" />
                                <span className="italic">{ev.text}</span>
                              </div>
                            )}
                            {ev.kind === 'step' && (
                              <div className="flex items-center gap-1.5 text-gray-400 font-sans text-[10px] uppercase tracking-wide">
                                <span>step {ev.phase}</span>
                                {ev.note && <span className="text-gray-500">· {ev.note}</span>}
                              </div>
                            )}
                            {ev.kind === 'error' && (
                              <div className="flex items-center gap-1.5 text-red-600">
                                <X className="h-3 w-3" />
                                <span>{ev.message}</span>
                              </div>
                            )}
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                </div>
              )}

              {/* Threads tab — list existing conversations + start a new one */}
              {panelMode === 'side' && activeTab === 'threads' && (
                <div className="flex-1 overflow-y-auto px-3 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      newChat();
                      setActiveTab('chat');
                    }}
                    className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 mb-3 rounded-md text-xs font-medium text-white ${c.sendBtn}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New conversation
                  </button>
                  {threads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center gap-2 text-gray-400">
                      <MessageSquare className="h-8 w-8" />
                      <p className="text-xs max-w-[240px]">
                        No conversations yet. Start your first one above.
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {threads
                        .slice()
                        .sort((a, b) => b.createdAt - a.createdAt)
                        .map((t) => {
                          const msgCount = t.messages.length;
                          const isActive = t.id === threadId;
                          const preview = t.messages.find((m) => m.role === 'user')?.content ?? '';
                          return (
                            <li key={t.id}>
                              <button
                                type="button"
                                onClick={() => selectThread(t.id)}
                                disabled={loadingThreadId === t.id}
                                className={`w-full text-left px-2 py-2 rounded-md text-xs transition-colors disabled:opacity-50 ${
                                  isActive
                                    ? `${c.iconBg} border border-gray-200`
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`truncate font-medium ${isActive ? 'text-gray-900' : 'text-gray-800'}`}>
                                    {t.title || 'Untitled'}
                                  </span>
                                  {isActive && (
                                    <span className={`shrink-0 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${c.iconBg} ${c.iconFg}`}>
                                      active
                                    </span>
                                  )}
                                  {loadingThreadId === t.id && (
                                    <span className="shrink-0 text-[9px] text-gray-400">loading…</span>
                                  )}
                                </div>
                                {preview && preview !== t.title && (
                                  <div className="text-gray-500 text-[11px] truncate mt-0.5">
                                    {preview}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400 font-mono">
                                  <span>{new Date(t.createdAt).toISOString().slice(0, 10)}</span>
                                  <span>·</span>
                                  <span>{msgCount} msg{msgCount === 1 ? '' : 's'}</span>
                                  {(t.trace?.length ?? 0) > 0 && (
                                    <>
                                      <span>·</span>
                                      <span>{t.trace!.length} trace</span>
                                    </>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              )}

              {/* Messages (hidden when another tab is active in side mode) */}
              <div
                className={`flex-1 overflow-y-auto px-4 py-3 space-y-4 ${
                  panelMode === 'side' && activeTab !== 'chat' ? 'hidden' : ''
                }`}
              >
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                    <div className={`h-10 w-10 rounded-xl ${c.emptyIconBg} border ${c.emptyIconBorder} flex items-center justify-center`}>
                      <Bot className={`h-5 w-5 ${c.emptyIconFg}`} />
                    </div>
                    <p className="text-sm font-medium text-gray-900">Hey! I&apos;m {name}</p>
                    <p className="text-xs text-gray-400 max-w-[240px]">{intro}</p>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className="group/msg">
                    {msg.role === 'user' ? (
                      <div className="flex justify-end">
                        <div className={`max-w-[85%] ${c.userMsg} text-white rounded-2xl rounded-br-md px-3 py-2 text-sm whitespace-pre-wrap`}>
                          {msg.content}
                        </div>
                      </div>
                    ) : (
                      <div>
                        {msg.content ? (
                          <div className={`prose prose-sm max-w-none text-gray-800 text-sm
                            [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                            [&_p]:my-1.5 [&_p]:leading-relaxed
                            [&_ul]:my-1.5 [&_ol]:my-1.5 [&_li]:my-0.5
                            [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm
                            [&_code]:bg-gray-800 [&_code]:text-amber-300 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs
                            [&_pre]:bg-gray-900 [&_pre]:rounded-lg [&_pre]:p-2 [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0
                            [&_strong]:text-gray-900 [&_strong]:font-semibold
                            [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-500
                            ${variant === 'green'
                              ? '[&_a]:text-green-600 [&_blockquote]:border-green-300'
                              : '[&_a]:text-blue-600 [&_blockquote]:border-blue-300'}`}
                          >
                            <Markdown remarkPlugins={[remarkGfm]}>{normalizeAgentMarkdown(msg.content)}</Markdown>
                          </div>
                        ) : isStreaming && i === messages.length - 1 ? (
                          <div className="flex items-center gap-1.5 py-1">
                            <span className={`w-1.5 h-1.5 ${c.typingDot} rounded-full animate-bounce [animation-delay:0ms]`} />
                            <span className={`w-1.5 h-1.5 ${c.typingDot} rounded-full animate-bounce [animation-delay:150ms]`} />
                            <span className={`w-1.5 h-1.5 ${c.typingDot} rounded-full animate-bounce [animation-delay:300ms]`} />
                          </div>
                        ) : null}
                        {msg.content && !(isStreaming && i === messages.length - 1) && (
                          <div className="opacity-0 group-hover/msg:opacity-100 transition-opacity mt-0.5">
                            <CopyButton text={msg.content} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input (hidden when refs tab is active) */}
              <div
                className={`shrink-0 border-t border-gray-100 px-3 py-2.5 ${
                  panelMode === 'side' && activeTab !== 'chat' ? 'hidden' : ''
                }`}
              >
                <div className={`flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 ${c.focus} focus-within:ring-1 transition-all`}>
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={finalPlaceholder}
                    className="flex-1 bg-transparent border-none outline-none resize-none text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 min-h-[20px] max-h-[120px] py-0"
                    rows={1}
                    disabled={isStreaming}
                  />
                  {isStreaming ? (
                    <button
                      onClick={stopStreaming}
                      className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Stop"
                    >
                      <Square className="h-3.5 w-3.5 fill-current" />
                    </button>
                  ) : (
                    <button
                      onClick={() => sendMessage()}
                      disabled={!input.trim()}
                      className={`shrink-0 p-1.5 rounded-lg ${c.sendBtn} text-white disabled:opacity-30 disabled:bg-gray-200 disabled:text-gray-400 transition-colors`}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Page-specific floating bubble */}
      <button
        onPointerDown={handleDragPointerDown}
        onPointerMove={handleDragPointerMove}
        onPointerUp={handleDragPointerUp}
        onPointerCancel={handleDragPointerUp}
        onClick={(e) => {
          if (justDraggedRef.current) {
            justDraggedRef.current = false;
            e.preventDefault();
            return;
          }
          setOpen(!open);
          setMinimized(false);
        }}
        style={{ right: `${position.right}px`, bottom: `${position.bottom}px` }}
        className={`fixed z-[45] h-14 w-14 rounded-full ${c.bubble} text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-colors touch-none select-none ${
          isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab hover:scale-105 active:scale-95'
        }`}
        title="Drag to move, click to open"
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <>
            <Bot className="h-6 w-6" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unread}
              </span>
            )}
          </>
        )}
      </button>
    </>
  );
}
